import { useTracking } from '@/contexts/TrackingContext';
import { useEffect } from 'react';

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        fbq?: (...args: any[]) => void;
    }
}

export default function TrackingScripts() {
    const { config, isServiceEnabled, pushToDataLayer } = useTracking();

    useEffect(() => {
        if (!config?.enabled) return;

        // Defer all tracking-script injection until the browser is idle.
        // GTM + Facebook together consume ~1s of main-thread time. Running
        // them eagerly after hydration contends with LCP image decoding and
        // inflates TBT. Idle-callback execution moves them out of the LCP
        // critical window while still firing within a few hundred ms in
        // practice. Falls back to a 1.5s setTimeout on browsers without
        // requestIdleCallback (Safari < 16.4).
        const initializeTracking = () => {

        // Initialize Google Tag Manager
        if (isServiceEnabled('googleTagManager') && config.services?.googleTagManager) {
            const { containerId } = config.services.googleTagManager;

            // GTM Script
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

            // Initialize dataLayer before GTM
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js',
            });

            document.head.appendChild(script);

            if (config.debug) {
                console.log(`[Tracking] Google Tag Manager initialized: ${containerId}`);
            }
        }

        // Initialize Google Analytics 4 (if not using GTM)
        if (isServiceEnabled('googleAnalytics') && config.services?.googleAnalytics && !isServiceEnabled('googleTagManager')) {
            const { measurementId } = config.services.googleAnalytics;

            // GA4 Script
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
            document.head.appendChild(script);

            // Initialize gtag
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
            window.gtag('js', new Date());
            window.gtag('config', measurementId, {
                anonymize_ip: config.privacy?.anonymize_ip,
            });

            if (config.debug) {
                console.log(`[Tracking] Google Analytics 4 initialized: ${measurementId}`);
            }
        }

        // Initialize Facebook Pixel
        if (isServiceEnabled('facebookPixel') && config.services?.facebookPixel) {
            const { pixelId } = config.services.facebookPixel;

            // Facebook Pixel Script — wraps fbq at SDK-loader level so init
            // calls are deduped regardless of caller (us, GTM, or any other
            // script). Without this, GTM's pixel tag + our React init both
            // fire fbq('init', X) → '[Meta Pixel] - Duplicate Pixel ID' warning.

            void (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    // Dedupe init at the fbq layer so any caller is covered.
                    if (arguments[0] === 'init') {
                        f.__fbqInited = f.__fbqInited || {};
                        if (f.__fbqInited[arguments[1]]) return;
                        f.__fbqInited[arguments[1]] = true;
                    }
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [] as any[];
                t = b.createElement(e) as HTMLScriptElement;
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode!.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

            window.fbq!('init', pixelId);
            window.fbq!('track', 'PageView');

            if (config.debug) {
                console.log(`[Tracking] Facebook Pixel initialized: ${pixelId}`);
            }
        }

        // Initialize Google Ads
        if (isServiceEnabled('googleAds') && config.services?.googleAds && window.gtag) {
            const { conversionId } = config.services.googleAds;
            window.gtag('config', conversionId);

            if (config.debug) {
                console.log(`[Tracking] Google Ads initialized: ${conversionId}`);
            }
        }

        }; // end initializeTracking

        // Idle-callback scheduling. The 3s timeout caps how long we wait
        // before forcing a run; in practice idle fires within ~50-200 ms of
        // hydration completing.
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            const handle = window.requestIdleCallback(initializeTracking, { timeout: 3000 });
            return () => {
                if (typeof window.cancelIdleCallback === 'function') {
                    window.cancelIdleCallback(handle);
                }
            };
        }
        const handle = setTimeout(initializeTracking, 1500);
        return () => clearTimeout(handle);
    }, [config, isServiceEnabled]);

    // Track page views on navigation
    useEffect(() => {
        if (!config?.enabled) return;

        // Track page view
        pushToDataLayer({
            event: 'pageview',
            page: {
                path: window.location.pathname,
                url: window.location.href,
                title: document.title,
            },
        });

        // Track with Google Analytics
        if (window.gtag && isServiceEnabled('googleAnalytics')) {
            window.gtag('event', 'page_view', {
                page_location: window.location.href,
                page_path: window.location.pathname,
                page_title: document.title,
            });
        }

        // Track with Facebook Pixel
        if (window.fbq && isServiceEnabled('facebookPixel')) {
            window.fbq('track', 'PageView');
        }
        // The dependency is read at render time, which also runs in node (SSR, phase 15).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeof window !== 'undefined' ? window.location.pathname : '']); // Track on route change

    // No visual output, just script loading
    return null;
}
